import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { contentApi, type ListSegmentsParams } from '@/api/content'
import type { QuizAttemptInput, QuizOwnerType, Section } from '@/api/types'

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => contentApi.getMe(), staleTime: 5 * 60_000 })
}

/** All audiences (ordered) — reference data for badges + dropdowns. Cached a while. */
export function useAudiences() {
  return useQuery({
    queryKey: ['audiences'],
    queryFn: () => contentApi.listAudiences(),
    staleTime: 5 * 60_000,
  })
}

export function useTracks(section?: Section) {
  return useQuery({
    queryKey: ['tracks', section ?? null],
    queryFn: () => contentApi.listTracks(section),
  })
}

export function useTrack(slug: string) {
  return useQuery({ queryKey: ['track', slug], queryFn: () => contentApi.getTrack(slug) })
}

export function useSegments(params: ListSegmentsParams) {
  return useQuery({
    queryKey: ['segments', params],
    queryFn: () => contentApi.listSegments(params),
    placeholderData: keepPreviousData,
  })
}

export function useSegment(slug: string) {
  return useQuery({ queryKey: ['segment', slug], queryFn: () => contentApi.getSegment(slug) })
}

/** The current user's "Your Journey" dashboard (progress + XP). */
export function useUserProgress() {
  return useQuery({ queryKey: ['user-progress'], queryFn: () => contentApi.getUserProgress() })
}

/**
 * The learner's badge catalog (their section) + earned status. Reading this also
 * grants any newly-met badges server-side; `newlyEarned` drives the celebration.
 */
export function useBadges() {
  return useQuery({ queryKey: ['badges'], queryFn: () => contentApi.getBadges() })
}

/** The current user's XP leaderboard, auto-scoped server-side. */
export function useLeaderboard() {
  return useQuery({ queryKey: ['leaderboard'], queryFn: () => contentApi.getLeaderboard() })
}

/** A specific client org's leaderboard (staff drill-in; 403 for clients). */
export function useOrgLeaderboard(orgId: string) {
  return useQuery({
    queryKey: ['leaderboard', 'org', orgId],
    queryFn: () => contentApi.getOrgLeaderboard(orgId),
    enabled: '' !== orgId,
  })
}

/** Client orgs for the staff "pick a client" leaderboard picker (staff only). */
export function useClientOrgs(enabled = true) {
  return useQuery({
    queryKey: ['client-orgs'],
    queryFn: () => contentApi.listClientOrgs(),
    enabled,
    staleTime: 60_000,
  })
}

/** Client orgs ranked by engagement — the staff analytics overview (staff only). */
export function useClientsAnalytics(enabled = true) {
  return useQuery({
    queryKey: ['analytics', 'clients'],
    queryFn: () => contentApi.listClientsAnalytics(),
    enabled,
    staleTime: 60_000,
  })
}

/** One org's training analytics (staff any org; customer_admin own org). */
export function useOrgAnalytics(orgId: string) {
  return useQuery({
    queryKey: ['analytics', 'org', orgId],
    queryFn: () => contentApi.getOrgAnalytics(orgId),
    enabled: '' !== orgId,
  })
}

// ---- Team Assignments ----

/** An org's assignments + completion (admin dashboard; staff or own-org admin). */
export function useOrgAssignments(orgId: string, enabled = true) {
  return useQuery({
    queryKey: ['assignments', 'org', orgId],
    queryFn: () => contentApi.listOrgAssignments(orgId),
    enabled: enabled && '' !== orgId,
  })
}

/** An org's members for the assignee picker (staff or own-org admin). */
export function useOrgMembers(orgId: string, enabled = true) {
  return useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => contentApi.listOrgMembers(orgId),
    enabled: enabled && '' !== orgId,
    staleTime: 60_000,
  })
}

/** The current learner's own assignments ("My Assignments"). */
export function useUserAssignments(enabled = true) {
  return useQuery({
    queryKey: ['assignments', 'user'],
    queryFn: () => contentApi.listUserAssignments(),
    enabled,
  })
}

export function useSearch(q: string, section?: Section) {
  const term = q.trim()
  return useQuery({
    queryKey: ['search', term, section ?? null],
    queryFn: () => contentApi.search(term, section),
    enabled: term.length > 0,
    placeholderData: keepPreviousData,
  })
}

// ---- Knowledgebase ----

/** Browse articles grouped by collection. */
export function useArticles(enabled = true) {
  return useQuery({
    queryKey: ['articles'],
    queryFn: () => contentApi.listArticles(),
    enabled,
  })
}

/** One article (the reader). */
export function useArticle(slug: string) {
  return useQuery({
    queryKey: ['article', slug],
    queryFn: () => contentApi.getArticle(slug),
    enabled: '' !== slug,
  })
}

/**
 * Ask the KB AI agent. Modelled as a query keyed by the question so each distinct
 * question fires one (rate-limited) call and is cached — the search bar and the KB
 * page reuse the same cache. Only fires for a non-empty question when enabled.
 */
export function useAskKb(question: string, enabled = true) {
  const q = question.trim()
  return useQuery({
    queryKey: ['kb-ask', q],
    queryFn: () => contentApi.askKb(q),
    enabled: enabled && q.length > 0,
    staleTime: 10 * 60_000,
    retry: false,
  })
}

/** The learner-facing quiz for a segment/track (answers hidden). */
export function useQuiz(ownerType: QuizOwnerType, slug: string, enabled = true) {
  return useQuery({
    queryKey: ['quiz', ownerType, slug],
    queryFn: () => contentApi.getQuiz(ownerType, slug),
    enabled: enabled && '' !== slug,
  })
}

/**
 * Submit a quiz answer. On success, refreshes the owner's detail so its
 * completion gate (which now folds in the quiz) updates on the page — plus the
 * learner read models a pass can change (Journey progress/XP, assignment
 * status, badge awards), so nothing shows stale after the final question.
 */
export function useSubmitQuizAttempt(ownerType: QuizOwnerType, slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: QuizAttemptInput) => contentApi.submitQuizAttempt(ownerType, slug, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quiz', ownerType, slug] })
      void qc.invalidateQueries({ queryKey: [ownerType === 'segment' ? 'segment' : 'track', slug] })
      void qc.invalidateQueries({ queryKey: ['user-progress'] })
      void qc.invalidateQueries({ queryKey: ['assignments', 'user'] })
      void qc.invalidateQueries({ queryKey: ['badges'] })
    },
  })
}
